import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { UserPlus, X, Plus, Loader2, Trash2 } from 'lucide-react';

const InviteTeamMemberModal = ({ eventId, isOpen, onClose, onSuccess }) => {
  const [teamMembers, setTeamMembers] = useState([
    { email: '', name: '', role: 'VIEWER' }
  ]);
  const { showSuccess, showError } = useToast();

  // Invite team members mutation
  const inviteMutation = useMutation({
    mutationFn: (members) => ApiService.inviteTeamMembers(eventId, members),
    onSuccess: (response) => {
      const successCount = response.filter(r => r.status === 'SUCCESS').length;
      const failureCount = response.filter(r => r.status !== 'SUCCESS').length;
      
      if (failureCount > 0) {
        showSuccess(`${successCount} member(s) invited successfully. ${failureCount} failed.`);
      } else {
        showSuccess(`Successfully invited ${successCount} team member(s)!`);
      }
      
      handleClose();
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      showError('Failed to invite team members. Please try again.');
      console.error('Invite error:', error);
    }
  });

  const handleAddMember = () => {
    setTeamMembers([...teamMembers, { email: '', name: '', role: 'VIEWER' }]);
  };

  const handleRemoveMember = (index) => {
    if (teamMembers.length > 1) {
      setTeamMembers(teamMembers.filter((_, i) => i !== index));
    }
  };

  const handleMemberChange = (index, field, value) => {
    const updated = [...teamMembers];
    updated[index][field] = value;
    setTeamMembers(updated);
  };

  const handleInvite = () => {
    // Validate
    const validMembers = teamMembers.filter(m => m.email.trim() && m.name.trim());
    
    if (validMembers.length === 0) {
      showError('Please add at least one team member with email and name');
      return;
    }

    // Check for duplicate emails
    const emails = validMembers.map(m => m.email.toLowerCase());
    const duplicates = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicates.length > 0) {
      showError('Duplicate emails found. Please use unique email addresses.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validMembers.filter(m => !emailRegex.test(m.email));
    if (invalidEmails.length > 0) {
      showError('Please enter valid email addresses');
      return;
    }

    inviteMutation.mutate(validMembers);
  };

  const handleClose = () => {
    setTeamMembers([{ email: '', name: '', role: 'VIEWER' }]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <span>Invite Team Members</span>
          </DialogTitle>
          <DialogDescription>
            Add team members to collaborate on this event. They will receive an email with their login credentials.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            {teamMembers.map((member, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Team Member {index + 1}
                  </h4>
                  {teamMembers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`email-${index}`}>Email *</Label>
                    <Input
                      id={`email-${index}`}
                      type="email"
                      placeholder="member@example.com"
                      value={member.email}
                      onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`name-${index}`}>Name *</Label>
                    <Input
                      id={`name-${index}`}
                      type="text"
                      placeholder="John Doe"
                      value={member.name}
                      onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`role-${index}`}>Role</Label>
                    <Select
                      value={member.role}
                      onValueChange={(value) => handleMemberChange(index, 'role', value)}
                    >
                      <SelectTrigger id={`role-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VIEWER">Viewer</SelectItem>
                        <SelectItem value="EDITOR">Editor</SelectItem>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              onClick={handleAddMember}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Member
            </Button>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Role Permissions:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li><strong>Viewer:</strong> Can view registrations and generate QR scan links</li>
                <li><strong>Editor:</strong> Can edit event details, view and export registrations</li>
                <li><strong>Admin:</strong> Full access including team management and settings</li>
              </ul>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="flex space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={inviteMutation.isPending}
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleInvite}
            disabled={inviteMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {inviteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                <span>Inviting...</span>
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                <span>Invite Team Members</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTeamMemberModal;

